import type { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';

import { statusLabels, type WindowMode } from '../benchmarkMonitoring';
import { BENCH } from '../tokens';
import { STATUS_COLORS, StatusDot } from './ProgressBar';

export const CARD_SX = {
  bgcolor: BENCH.cardBg,
  border: `1px solid ${BENCH.divider}`,
  borderRadius: '12px',
  p: '24px 32px 8px',
  boxSizing: 'border-box',
  width: '100%',
} as const;

export const NAME_WIDTH = 200;
export const COUNT_WIDTHS = [88, 112, 112, 112] as const;
export const CHEVRON_WIDTH = 16;

export function CardHeading({ title, right, children }: { title: string; right?: ReactNode; children?: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', pb: '16px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <Typography component="h2" sx={{ fontFamily: BENCH.font, fontSize: '20px', lineHeight: '28px', fontWeight: 700, color: BENCH.ink, m: 0 }}>
          {title}
        </Typography>
        {children}
      </Box>
      {right !== undefined && (
        <Typography component="span" sx={{ fontFamily: BENCH.font, fontSize: '16px', lineHeight: '24px', fontWeight: 500, color: BENCH.muted }}>
          {right}
        </Typography>
      )}
    </Box>
  );
}

const HEADER_SX = {
  fontFamily: BENCH.font,
  fontSize: '12px',
  lineHeight: '16px',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: BENCH.muted,
} as const;

export function HeaderCell({ children, width, align = 'left', sx }: { children: ReactNode; width?: number; align?: 'left' | 'right'; sx?: object }) {
  return (
    <Box
      sx={{
        ...HEADER_SX,
        flex: width ? `0 0 ${width}px` : 1,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        gap: '8px',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export function StatusHeaderRow({ first, mode, chevron }: { first: string; mode: WindowMode; chevron?: boolean }) {
  const labels = statusLabels(mode);
  return (
    <Box data-testid="status-header" sx={{ display: 'flex', alignItems: 'center', gap: '16px', pb: '8px', borderBottom: `2px solid ${BENCH.headerRule}` }}>
      <HeaderCell width={NAME_WIDTH}>{first}</HeaderCell>
      <HeaderCell>Progress</HeaderCell>
      {labels.map((label, i) => (
        <HeaderCell key={label} width={COUNT_WIDTHS[i]} align="right">
          <StatusDot color={STATUS_COLORS[i] ?? STATUS_COLORS[0]} />
          {label}
        </HeaderCell>
      ))}
      {chevron && <Box sx={{ width: CHEVRON_WIDTH, flexShrink: 0 }} />}
    </Box>
  );
}

export function CountCell({ value, of, hot, dash, width, note }: { value: number; of?: number; hot?: boolean; dash?: boolean; width: number; note?: string }) {
  const color = dash || value === 0 ? BENCH.zero : hot ? BENCH.hot : BENCH.ink;
  return (
    <Box
      data-testid="count-cell"
      sx={{ flex: `0 0 ${width}px`, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', fontVariantNumeric: 'tabular-nums' }}
    >
      <Typography
        component="span"
        data-hot={hot && !dash && value > 0 ? 'true' : undefined}
        sx={{ fontFamily: BENCH.font, fontSize: '16px', lineHeight: '24px', fontWeight: hot && !dash && value > 0 ? 800 : 500, color }}
      >
        {dash ? '-' : value}
      </Typography>
      {of !== undefined && !dash && (
        <Typography component="span" sx={{ fontFamily: BENCH.font, fontSize: '12px', lineHeight: '16px', fontWeight: 400, color: BENCH.muted }}>
          {`of ${of}`}
        </Typography>
      )}
      {note !== undefined && (
        <Typography component="span" data-testid="closed-note" sx={{ fontFamily: BENCH.font, fontSize: '12px', lineHeight: '16px', fontWeight: 400, color: BENCH.muted, whiteSpace: 'nowrap' }}>
          {note}
        </Typography>
      )}
    </Box>
  );
}

export function NameCell({ name, sub, subHot }: { name: string; sub?: string; subHot?: boolean }) {
  return (
    <Box sx={{ flex: `0 0 ${NAME_WIDTH}px`, minWidth: 0 }}>
      <Typography component="span" sx={{ display: 'block', fontFamily: BENCH.font, fontSize: '16px', lineHeight: '24px', fontWeight: 700, color: BENCH.ink }}>
        {name}
      </Typography>
      {sub !== undefined && (
        <Typography
          component="span"
          sx={{ display: 'block', fontFamily: BENCH.font, fontSize: '12px', lineHeight: '16px', fontWeight: subHot ? 700 : 400, color: subHot ? BENCH.hot : BENCH.muted }}
        >
          {sub}
        </Typography>
      )}
    </Box>
  );
}

export const ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  py: '16px',
  borderBottom: `1px solid ${BENCH.divider}`,
  '&:last-of-type': { borderBottom: 0 },
} as const;
